import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { aschehougOidcClient } from '@/main'
import { userSessionState } from '@aschehoug/aschehoug-oidc-client/dist/AMTypes'
import { useRouter } from 'vue-router'
import { useKlossToast } from '@/composables/useKlossToast'
import { useUser } from '@/composables/useUser'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const klossToast = useKlossToast()

  const user = useUser()
  const isLoading = ref<boolean>(true)
  const isAuthenticated = computed(() => user.data.value !== null)

  const loginFeide = async () => {
    const feideUrl = `${import.meta.env.VITE_AM_CORE}/am/XUI/?service=Modus&realm=/&goto=${import.meta.env.VITE_HOST}/feide&gotoOnFail=${import.meta.env.VITE_ID_CLOUD}/feil#login`
    window.location.href = feideUrl
  }

  async function logout() {
    user.clear()
    localStorage.clear()
    await aschehougOidcClient.logout()
  }

  async function handleRedirectAfterLogin() {
    // Clean up the URL by removing oidc query parameters
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('iss')
    url.searchParams.delete('state')
    url.searchParams.delete('client_id')
    router.replace(`${url.pathname}?${url.searchParams.toString()}`)

    const redirectRoute = sessionStorage.getItem('modus-redirect-route')

    if (!redirectRoute) return

    router.replace(redirectRoute)
    sessionStorage.removeItem('modus-redirect-route')
  }

  const handleLogin = async () => {
    try {
      isLoading.value = true
      const sessionState = await aschehougOidcClient.startAuthSession()
      
      if (sessionState !== userSessionState.hasActiveSession) {
        if (!isAuthenticated.value) return
        return logout()
      }

      await user.init()

      handleRedirectAfterLogin()
    }
    catch (error) {
      console.error(error)
      klossToast.error('Logg-inn feilet.')
      await logout()
    }
    finally {
      isLoading.value = false
    }
  }

  watch(router.currentRoute, async () => {
    // try to log in on route change if not already logged in
    if (aschehougOidcClient.getAccessToken() || isLoading.value) return

    await handleLogin()
  })

  return {
    user: user.data,
    isTeacher: user.isTeacher,
    isStudent: user.isStudent,
    isInAschehougOrganization: user.isInAschehougOrganization,
    isAuthenticated,
    isLoading,
    logout,
    loginFeide,
    handleLogin,
  }
})
